import { CheckCircle } from "lucide-react";
import { PortalLayout } from "../components/PortalLayout";
import { platformLogoMap } from "../config/platforms";

const platforms = [
  {
    category: "SECURITY OPERATIONS",
    name: "AutoGuard",
    logoKey: "AutoGuard",
    tagline: "Security Operations & Workforce Management Platform",
    description:
      "A comprehensive security operations platform designed to modernise and streamline guard management, incident reporting, and real-time operational control. The platform provides both web-based administrative tools and mobile applications for field personnel, enabling full visibility across distributed security operations.",
    capabilities: [
      "Real-time guard tracking and shift management",
      "Incident reporting with structured documentation and audit trails",
      "Visitor and access control logging",
      "Mobile-enabled workforce interaction and reporting",
      "Centralised dashboard for operational oversight",
    ],
    value:
      "AutoGuard enables security companies and organisations to improve accountability, operational efficiency, and compliance, while reducing manual processes and increasing real-time visibility across all activities.",
  },
  {
    category: "DIGITAL PAYMENTS",
    name: "MiCash",
    logoKey: "MiCash",
    tagline: "Digital Payments & Financial Transaction Platform",
    description:
      "A secure and scalable digital payments platform designed to facilitate seamless financial transactions across individuals, businesses, and systems. The platform supports multiple payment channels and enables fast, reliable, and traceable financial interactions.",
    capabilities: [
      "Peer-to-peer payments and transfers",
      "Multi-method payment processing",
      "Payment requests and transaction tracking",
      "Fraud detection and transaction security controls",
      "Scalable financial transaction infrastructure",
    ],
    value:
      "MiCash provides a flexible and accessible financial platform that enables businesses and users to transact securely, reduce friction in payments, and support scalable financial ecosystems.",
  },
  {
    category: "LIFE-STAGE SERVICES",
    name: "Life Happens",
    logoKey: "Life Happens",
    tagline: "Life-Stage Digital Ecosystem",
    description:
      "A structured digital platform designed to guide individuals through key life events by integrating essential services into a single ecosystem. The platform connects users with legal, financial, and service providers, creating a seamless experience during critical life stages.",
    capabilities: [
      "Guided workflows for major life events (e.g. marriage, divorce, planning)",
      "Integration with service providers (legal, financial, insurance)",
      "Structured onboarding and data collection",
      "Digital documentation and process tracking",
      "Scalable mobile and web-based access",
    ],
    value:
      "Life Happens simplifies complex life processes by centralising services, improving user experience, and enabling service providers to engage with clients more efficiently.",
  },
  {
    category: "PRINT SERVICES",
    name: "Get Me Printing",
    logoKey: "Get Me Printing",
    tagline: "Distributed Print & Production Platform",
    description:
      "A digital platform enabling users to access printing and production services through a centralised, automated system. Built to support both public and enterprise use cases, the platform connects digital requests with physical output through integrated infrastructure.",
    capabilities: [
      "On-demand printing from web and mobile",
      "QR-based print execution",
      "Centralised print job management",
      "Integration with hardware and service providers",
      "Financial tracking and usage management",
    ],
    value:
      "Get Me Printing modernises traditional print services by providing accessibility, automation, and scalability, allowing users to manage printing needs efficiently across multiple locations.",
  },
];

export function PlatformEcosystemPage() {
  return (
    <PortalLayout>

      {/* Header */}
      <div className="mb-14">
        <p className="text-xs font-semibold uppercase tracking-widest text-[#7B1A1A] mb-4">
          Platform Ecosystem
        </p>
        <h1 className="text-2xl sm:text-4xl md:text-5xl font-semibold text-[#1F2933] mb-8 leading-tight max-w-3xl">
          A Portfolio of Scalable Digital Platforms
        </h1>
        <p className="text-[#4B5563] max-w-4xl leading-relaxed">
          LEO Group develops and manages a portfolio of proprietary digital platforms spanning security operations,
          digital payments, life-stage services, and distributed print solutions. Each platform is independently
          scalable within the group's unified infrastructure.
        </p>
      </div>

      {/* Platforms */}
      <div className="space-y-8 mb-14">
        {platforms.map((platform) => {
          const logoSrc = platform.logoKey ? platformLogoMap[platform.logoKey] : null;
          return (
            <div key={platform.name} className="bg-white border border-[#E5E7EB] rounded-lg p-8 shadow-sm hover:border-[#7B1A1A]/30 transition-colors">
              <div className="flex flex-col sm:flex-row items-start gap-4 mb-6">

                {/* Platform logo */}
                <div className="flex items-center justify-center h-24 flex-shrink-0 mb-3">
                  <img
                    src={logoSrc ?? "/logo.png"}
                    alt={platform.name}
                    className={`${platform.name === "Life Happens" ? "max-h-14" : "max-h-16"} w-auto object-contain`}
                    onError={(e) => { e.currentTarget.src = "/logo.png"; }}
                  />
                </div>

                <div className="flex-1">
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#7B1A1A] border border-[#7B1A1A]/30 px-3 py-1 rounded inline-block mb-3">
                    {platform.category}
                  </span>
                  <h3 className="text-2xl font-semibold text-[#1F2933] mb-1">{platform.name}</h3>
                  <p className="text-sm text-[#7B1A1A] mb-4">{platform.tagline}</p>
                  <p className="text-[#4B5563] text-sm leading-relaxed mb-6">{platform.description}</p>

                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-[#1F2933] mb-3">Key Capabilities</h4>
                    <ul className="space-y-2">
                      {platform.capabilities.map((cap) => (
                        <li key={cap} className="flex items-start gap-2 text-[#4B5563]">
                          <CheckCircle className="w-4 h-4 text-[#7B1A1A] flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{cap}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-[#FDEAE0]/60 border border-[#7B1A1A]/15 rounded-lg p-4">
                    <p className="text-sm text-[#4B5563]">
                      <span className="font-semibold text-[#1F2933]">Value Proposition: </span>
                      {platform.value}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Note */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 shadow-sm mb-14">
        <p className="text-sm text-[#4B5563] leading-relaxed">
          Each platform operates within the group's unified infrastructure and is designed for licensing and
          long-term revenue generation.
        </p>
      </div>

    </PortalLayout>
  );
}
